import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';

import PrimaryButton from '../ui/buttons/primaryButton';
import EventChangePreview from '../events/eventChangePreview';
import {
  applyPlayerReplacement,
  previewPlayerReplacement,
  searchEventPlayers
} from '../../features/event/eventSlice';
import { setAlertInfo } from '../../features/alert/alertSlice';
import { getGameByEventId } from '../../features/game/gameSlice';
import { getBracketsByEventId } from '../../features/bracket/bracketSlice';

const playerLabel = (player) =>
  [player?.name, player?.surname].filter(Boolean).join(' ').trim() || 'Unknown player';

const emptyNewPlayer = { name: '', surname: '', handicap: '', bsaNumber: '' };

/**
 * Replaces one player everywhere in an event. Two steps: choose the player and
 * the replacement, then review the server's preview before applying. The apply
 * request carries the preview hash, so a stale preview is refused rather than
 * applied blindly. Opened from a player row (source prefilled) or from the
 * event menu (source chosen here).
 */
const PlayerSwapDialog = ({ playerToSwap, open, onClose, eventId }) => {
  const dispatch = useDispatch();
  const event = useSelector((state) => state.event.event);

  const [step, setStep] = useState('setup');
  const [source, setSource] = useState(null);
  const [mode, setMode] = useState('existing');
  const [replacement, setReplacement] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [newPlayer, setNewPlayer] = useState(emptyNewPlayer);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);
  const [appliedResult, setAppliedResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const sourceCandidates = useMemo(() => {
    const byId = new Map();
    const add = (player, teamName) => {
      if (!player?.id) return;
      const existing = byId.get(player.id) || {
        id: player.id,
        name: player.name,
        surname: player.surname,
        handicap: player.handicap,
        bsaNumber: player.bsaNumber,
        status: player.status,
        teamNames: []
      };
      if (player.status) existing.status = player.status;
      if (teamName && !existing.teamNames.includes(teamName)) {
        existing.teamNames.push(teamName);
      }
      byId.set(player.id, existing);
    };

    (event?.players || []).forEach((player) => add(player));
    (event?.teams || []).forEach((team) => {
      (team.members || []).forEach((member) => add(member, team.name || 'Unnamed team'));
      (team.reserves || []).forEach((reserve) =>
        add(reserve, `Reserve: ${team.name || 'Unnamed team'}`)
      );
    });

    return [...byId.values()].sort(
      (left, right) =>
        String(left.surname || '').localeCompare(String(right.surname || '')) ||
        String(left.name || '').localeCompare(String(right.name || ''))
    );
  }, [event?.players, event?.teams]);

  const reset = () => {
    setStep('setup');
    setSource(null);
    setMode('existing');
    setReplacement(null);
    setSearchInput('');
    setOptions([]);
    setNewPlayer(emptyNewPlayer);
    setIncludeCompleted(false);
    setPreview(null);
    setLastPayload(null);
    setAppliedResult(null);
    setBusy(false);
    setErrorMessage('');
  };

  useEffect(() => {
    if (!open) return;
    reset();
    if (playerToSwap) {
      const candidate = sourceCandidates.find((item) => item.id === playerToSwap.id);
      setSource({
        id: playerToSwap.id,
        name: playerToSwap.name,
        surname: playerToSwap.surname,
        handicap: playerToSwap.handicap,
        bsaNumber: playerToSwap.bsaNumber,
        status: playerToSwap.status,
        teamNames: candidate?.teamNames || []
      });
    }
    // Reset only when the dialog opens or a different source row is used.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, playerToSwap]);

  useEffect(() => {
    if (!open || step !== 'setup' || mode !== 'existing') {
      return undefined;
    }

    const query = searchInput.trim();
    if (query.length < 2) {
      setOptions([]);
      setSearching(false);
      return undefined;
    }

    let active = true;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const result = await dispatch(searchEventPlayers({ eventId, query, limit: 25 })).unwrap();
        if (active) {
          setOptions(result.players || []);
        }
      } catch (error) {
        if (active) {
          setOptions([]);
        }
      } finally {
        if (active) {
          setSearching(false);
        }
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [open, step, mode, searchInput, dispatch, eventId]);

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const buildPayload = () => {
    const payload = {
      oldPlayerId: source?.id,
      includeCompleted
    };

    if (mode === 'existing') {
      payload.newPlayerId = replacement?.id;
    } else {
      payload.newPlayer = {
        name: newPlayer.name.trim(),
        surname: newPlayer.surname.trim(),
        handicap: Number(newPlayer.handicap) || 0,
        bsaNumber: newPlayer.bsaNumber.trim()
      };
    }

    return payload;
  };

  const canPreview =
    Boolean(source?.id) &&
    (mode === 'existing'
      ? Boolean(replacement?.id)
      : Boolean(newPlayer.name.trim()) && Boolean(newPlayer.surname.trim()));

  const runPreview = async (payload) => {
    setBusy(true);
    setErrorMessage('');
    try {
      const result = await dispatch(previewPlayerReplacement({ eventId, payload })).unwrap();
      if (result.alreadyApplied) {
        reset();
        onClose?.();
        dispatch(setAlertInfo('That player replacement has already been applied to this event.'));
        return;
      }
      setPreview(result);
      setLastPayload(payload);
      setStep('review');
    } catch (error) {
      setErrorMessage(error?.message || 'The preview could not be generated.');
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = () => {
    if (!canPreview) return;
    runPreview(buildPayload());
  };

  const handleApply = async () => {
    if (!preview?.previewHash || !lastPayload) return;
    setBusy(true);
    setErrorMessage('');
    try {
      const result = await dispatch(
        applyPlayerReplacement({
          eventId,
          payload: { ...lastPayload, previewHash: preview.previewHash }
        })
      ).unwrap();

      if (result.alreadyApplied) {
        reset();
        onClose?.();
        dispatch(setAlertInfo('That player replacement has already been applied to this event.'));
        return;
      }

      // Keep the dialog open on the applied summary so the organiser can see
      // exactly what changed; closing it is the only way to dismiss it.
      setAppliedResult(result);
      setStep('done');
      dispatch(getGameByEventId(eventId));
      dispatch(getBracketsByEventId(eventId));
    } catch (error) {
      if (error?.code === 'EVENT_CHANGED') {
        try {
          const fresh = await dispatch(
            previewPlayerReplacement({ eventId, payload: lastPayload })
          ).unwrap();
          setPreview(fresh);
          dispatch(
            setAlertInfo('The event changed since the preview. The preview has been refreshed.')
          );
        } catch (previewError) {
          setErrorMessage(previewError?.message || 'The preview could not be refreshed.');
        }
      } else if (error?.code === 'NO_CHANGES' || error?.alreadyApplied) {
        reset();
        onClose?.();
        dispatch(setAlertInfo(error.message || 'There is nothing left to change.'));
      } else {
        const partial = error?.details?.partialFailure
          ? ' Some records were already written — preview again before retrying.'
          : '';
        setErrorMessage(`${error?.message || 'The replacement failed.'}${partial}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const isReview = step === 'review';
  const isDone = step === 'done';
  let caption = 'One player, everywhere in this event.';
  if (isReview) caption = 'Review the changes before applying.';
  if (isDone) caption = 'Summary of what changed.';

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h6" color="primary">
          {isDone ? 'Replacement applied' : 'Replace Player'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        {errorMessage ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        ) : null}

        {isDone ? (
          <EventChangePreview
            result={{
              ...preview,
              counts: appliedResult?.counts || preview?.counts,
              warnings: appliedResult?.warnings || [],
              errors: [],
              alreadyApplied: false
            }}
            kind="player"
            applied
          />
        ) : !isReview ? (
          <Stack gap={2}>
            <Autocomplete
              options={sourceCandidates}
              value={source}
              onChange={(event, value) => setSource(value)}
              getOptionLabel={(option) => playerLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={Boolean(playerToSwap)}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{playerLabel(option)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.teamNames?.length
                        ? option.teamNames.join(', ')
                        : 'On the event roster only'}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Player to replace"
                  helperText={
                    playerToSwap
                      ? 'Opened from this player.'
                      : 'Search the event roster, team members and reserves.'
                  }
                />
              )}
            />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Replacement
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={mode}
                onChange={(event, value) => value && setMode(value)}
                sx={{ mb: 2 }}>
                <ToggleButton value="existing">Existing player</ToggleButton>
                <ToggleButton value="new">New player</ToggleButton>
              </ToggleButtonGroup>

              {mode === 'existing' ? (
                <Autocomplete
                  options={options}
                  value={replacement}
                  onChange={(event, value) => setReplacement(value)}
                  inputValue={searchInput}
                  onInputChange={(event, value) => setSearchInput(value)}
                  getOptionLabel={(option) => playerLabel(option)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(items) => items}
                  loading={searching}
                  noOptionsText={
                    searchInput.trim().length < 2
                      ? 'Type at least 2 characters'
                      : 'No players found'
                  }
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{playerLabel(option)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[
                            option.handicap ? `HCP ${option.handicap}` : null,
                            option.bsaNumber ? `BSA ${option.bsaNumber}` : null,
                            option.status,
                            option.teamName || (option.inEvent ? 'In this event' : 'Directory')
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search directory and event players"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searching ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              ) : (
                <Stack gap={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                    <TextField
                      label="Name*"
                      value={newPlayer.name}
                      onChange={(event) =>
                        setNewPlayer((current) => ({ ...current, name: event.target.value }))
                      }
                      fullWidth
                    />
                    <TextField
                      label="Surname*"
                      value={newPlayer.surname}
                      onChange={(event) =>
                        setNewPlayer((current) => ({ ...current, surname: event.target.value }))
                      }
                      fullWidth
                    />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                    <TextField
                      label="Handicap"
                      type="number"
                      value={newPlayer.handicap}
                      onChange={(event) =>
                        setNewPlayer((current) => ({ ...current, handicap: event.target.value }))
                      }
                      fullWidth
                    />
                    <TextField
                      label="BSA Number"
                      value={newPlayer.bsaNumber}
                      onChange={(event) =>
                        setNewPlayer((current) => ({ ...current, bsaNumber: event.target.value }))
                      }
                      fullWidth
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    A matching directory player is reused. Otherwise they are added to the
                    directory without email or phone details.
                  </Typography>
                </Stack>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={includeCompleted}
                  onChange={(event) => setIncludeCompleted(event.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Include completed records
                  <Typography variant="caption" color="text.secondary" display="block">
                    Also update completed games, finalised section records and completed bracket
                    matches.
                  </Typography>
                </Typography>
              }
            />
          </Stack>
        ) : (
          <EventChangePreview result={preview} kind="player" />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {isDone ? (
          <Button onClick={handleClose}>Close</Button>
        ) : !isReview ? (
          <>
            <Button onClick={handleClose} disabled={busy}>
              Cancel
            </Button>
            <PrimaryButton
              variant="contained"
              onClick={handlePreview}
              disabled={!canPreview || busy}>
              {busy ? <CircularProgress size={20} /> : 'Preview changes'}
            </PrimaryButton>
          </>
        ) : (
          <>
            <Button onClick={() => setStep('setup')} disabled={busy}>
              Back
            </Button>
            <PrimaryButton
              variant="contained"
              onClick={handleApply}
              disabled={busy || !preview?.previewHash || Boolean(preview?.blocked)}>
              {busy ? <CircularProgress size={20} /> : 'Apply replacement'}
            </PrimaryButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PlayerSwapDialog;
